import { useState, useEffect, useCallback, useMemo } from "react";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import { formatDisplayDate } from "../../../../util/dateFormat";
import { Button, Box, IconButton } from "@mui/material";
import { OverviewHeaderBar } from "../../../common/OverviewHeaderBar";
import { ScrollableDataGrid } from "../../../common/ScrollableDataGrid";
import { endpoints } from "../../../../constants/Endpoints";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

export function PropertyTable() {
  const toggleHighlight = async (row) => {
    const updated = {
      ...row,
      extensionIsHighlighted: !row.extensionIsHighlighted,
    };
    await updateData(updated);
    refreshData();
  };

  const columns = useMemo(
    () => [
      {
        field: "extensionIsHighlighted",
        headerName: "Uitgelicht",
        width: 110,
        sortable: true,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleHighlight(params.row);
            }}
          >
            {params.row.extensionIsHighlighted ? (
              <StarIcon color="warning" fontSize="inherit" />
            ) : (
              <StarBorderIcon fontSize="inherit" />
            )}
          </IconButton>
        ),
      },
      { field: "street", headerName: "Straat", flex: 1, minWidth: 200 },
      { field: "number", headerName: "Nr.", width: 80 },
      { field: "addition", headerName: "Toevoeging", width: 110 },
      { field: "city", headerName: "Stad", flex: 0.8, minWidth: 140 },
      {
        field: "price",
        headerName: "Prijs",
        width: 130,
        valueFormatter: (value) => (value != null ? `€ ${value}` : ""),
      },
      {
        field: "startDateAvailability",
        headerName: "Ingangsdatum",
        width: 150,
        // Removed valueGetter to avoid accessing undefined param in edge cases; render safely with fallbacks
        renderCell: (params) => {
          const raw = params?.row?.startDateAvailability;
          if (!raw) return <span>-</span>;
          // Handle literal text like 'Per direct'
          if (typeof raw === "string" && raw.toLowerCase() === "per direct") {
            return <span>Per direct</span>;
          }
          let formatted = raw;
          try {
            formatted = formatDisplayDate(raw) || raw;
          } catch (e) {}
          return <span>{formatted}</span>;
        },
      },
    ],
    []
  );

  const refreshData = () => {
    setLoading(true);
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });
  };

  const styles = {
    exportToCsvButton: {
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        color: "#d01717",
      },
    },
    exportToCsvLink: {
      color: "white",
      textDecoration: "none",
    },
    newButton: {
      backgroundColor: "#eb008b",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    refreshButton: {
      backgroundColor: "#f68121",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      marginLeft: "20px",
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
  };

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.properties.base,
      null
    );
    return await response.json();
  }, []);

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const [isMounted, setMounted] = useState(true);

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });

    return () => {
      setMounted(false);
    };
  }, [isMounted, getData]);

  const updateData = async (newData) => {
    await HttpClient.doRequest(
      Methods.POST,
      endpoints.properties.base,
      newData
    );
  };

  return (
    <Box>
      <OverviewHeaderBar
        title="Huidig woningaanbod"
        actions={
          <Button
            variant="contained"
            style={styles.refreshButton}
            startIcon={<RefreshIcon />}
            onClick={refreshData}
          >
            <span>Vernieuw</span>
          </Button>
        }
      />
      <Box sx={{ mx: 3, mb: 3 }}>
        <ScrollableDataGrid
          rows={data}
          columns={columns}
          loading={isLoading}
          getRowId={(r) =>
            r.houseId || r.HouseId || r.id || r.propertyId || r.extensionId
          }
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          dataGridSx={{ backgroundColor: "background.paper" }}
        />
      </Box>
    </Box>
  );
}
